import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Spinner, Row, Col, Alert, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Select from 'react-select';

/**
 * Registers received units of a PO line as individual assets. Shared fields apply to every
 * unit; serial numbers are entered one-per-line. Pasting two spreadsheet columns
 * (serial <tab> asset ID) fills the optional per-unit Asset ID too, and each row's ID stays
 * editable in the preview table. Blank Asset IDs are auto-generated server-side.
 *
 * Props: isOpen, toggle, po, line, locations, onRegister(payload), isSubmitting, apiError
 */
const RegisterAssetsModal = ({ isOpen, toggle, po, line, locations = [], onRegister, isSubmitting = false, apiError = '' }) => {
  const { t } = useTranslation();
  const [assetName, setAssetName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [locationId, setLocationId] = useState(null);
  const [assetStatus, setAssetStatus] = useState('ONLINE');
  const [serialText, setSerialText] = useState('');
  const [manualIds, setManualIds] = useState({});
  const [error, setError] = useState('');

  const received = line?.receivedQuantity ?? 0;
  const registered = line?.registeredAssetCount ?? 0;
  const remaining = Math.max(0, received - registered);

  useEffect(() => {
    if (isOpen) {
      setAssetName(line?.description || '');
      setManufacturer(''); setModelNumber(''); setLocationId(null); setAssetStatus('ONLINE');
      setSerialText(''); setManualIds({}); setError('');
    }
  }, [isOpen, line]);

  const locationOptions = locations.map((l) => ({ value: l.id, label: l.locationName }));

  // Each line: "SERIAL" or "SERIAL<tab>ASSET_ID" (a two-column spreadsheet paste).
  const rows = useMemo(() => serialText.split('\n')
    .map((l) => l.trim()).filter(Boolean)
    .map((l) => {
      const [serial, pastedId] = l.split('\t').map((s) => (s || '').trim());
      return { serial, pastedId: pastedId || '' };
    })
    .filter((r) => r.serial), [serialText]);

  const effectiveId = (r) => (manualIds[r.serial] !== undefined ? manualIds[r.serial] : r.pastedId);

  const handleSubmit = () => {
    if (rows.length === 0) { setError(t('planning_purchasing.serials_required', 'Enter at least one serial number')); return; }
    if (rows.length > remaining) { setError(t('planning_purchasing.too_many_units', 'You can register at most {{n}} units', { n: remaining })); return; }
    const serials = rows.map((r) => r.serial);
    const dupes = serials.filter((s, i) => serials.findIndex((x) => x.toLowerCase() === s.toLowerCase()) !== i);
    if (dupes.length) { setError(t('planning_purchasing.dup_serials', 'Duplicate serial numbers: {{s}}', { s: dupes[0] })); return; }
    const ids = rows.map((r) => effectiveId(r)).filter(Boolean);
    const dupIds = ids.filter((s, i) => ids.findIndex((x) => x.toLowerCase() === s.toLowerCase()) !== i);
    if (dupIds.length) { setError(t('planning_purchasing.dup_asset_ids', 'Duplicate asset IDs: {{s}}', { s: dupIds[0] })); return; }
    onRegister({
      purchaseOrderId: po.id, lineItemId: line.id,
      assetName: assetName || null, manufacturer: manufacturer || null, modelNumber: modelNumber || null,
      currentLocation: locationId, vendorId: po.vendorId ?? null,
      assetStatus,
      units: rows.map((r) => ({ serialNumber: r.serial, assetNumber: effectiveId(r) || null })),
    });
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
        <div className="modal-header">
          <h5 className="modal-title">{t('planning_purchasing.register_assets', 'Register Assets')}</h5>
          <button type="button" className="btn-close" onClick={toggle} />
        </div>
        <div className="modal-body">
          <div className="alert alert-info py-2">
            {t('planning_purchasing.register_summary', '{{reg}} of {{rec}} received units registered.', { reg: registered, rec: received })}
            {' '}<strong>{t('planning_purchasing.remaining_to_register', '{{n}} remaining', { n: remaining })}</strong>
          </div>
          {(apiError || error) && <Alert color={apiError ? 'danger' : 'warning'}>{apiError || error}</Alert>}
          <Row className="mb-2">
            <Col md={6}><label className="form-label small">{t('planning_purchasing.asset_name', 'Asset Name')}</label>
              <input className="form-control" value={assetName} onChange={(e) => setAssetName(e.target.value)} /></Col>
            <Col md={6}><label className="form-label small">{t('shared.location', 'Location')}</label>
              <Select options={locationOptions} value={locationOptions.find((o) => o.value === locationId) || null}
                      onChange={(o) => setLocationId(o?.value || null)} isClearable placeholder={t('shared.select_location', 'Select location')} classNamePrefix="react-select" /></Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}><label className="form-label small">{t('planning_purchasing.manufacturer', 'Manufacturer')}</label>
              <input className="form-control" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} /></Col>
            <Col md={6}><label className="form-label small">{t('planning_purchasing.model_reference', 'Model / Reference')}</label>
              <input className="form-control" value={modelNumber} onChange={(e) => setModelNumber(e.target.value)} /></Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}><label className="form-label small">{t('shared.asset_status', 'Asset Status')}</label>
              <select className="form-select" value={assetStatus} onChange={(e) => setAssetStatus(e.target.value)}>
                <option value="ONLINE">{t('shared.online', 'Online')}</option>
                <option value="OFFLINE">{t('shared.offline', 'Offline')}</option>
                <option value="DO_NOT_TRACK">{t('shared.do_not_track', 'Do Not Track')}</option>
              </select></Col>
          </Row>
          <label className="form-label fw-medium">
            {t('planning_purchasing.serials_label', 'Serial numbers (one per line — paste one or two spreadsheet columns: serial, asset ID)')}
            <span className="text-muted ms-2">{rows.length}/{remaining}</span>
          </label>
          <textarea className="form-control" rows={6} value={serialText} onChange={(e) => setSerialText(e.target.value)}
                    placeholder={"SN-0001\nSN-0002\nSN-0003"} disabled={remaining <= 0} />
          {rows.length > 0 && (
            <div className="table-responsive mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
              <Table size="sm" bordered className="align-middle mb-0">
                <thead className="table-light"><tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{t('shared.serial_number', 'Serial Number')}</th>
                  <th>{t('shared.asset_id', 'Asset ID')}</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.serial}-${i}`}>
                      <td className="text-muted">{i + 1}</td>
                      <td>{r.serial}</td>
                      <td>
                        <input className="form-control form-control-sm" value={effectiveId(r)}
                               placeholder={t('shared.enter_asset_id', 'Enter asset ID (auto-generated if empty)')}
                               onChange={(e) => setManualIds((p) => ({ ...p, [r.serial]: e.target.value }))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting || remaining <= 0}>
            {isSubmitting ? <><Spinner size="sm" className="me-2" />{t('shared.saving', 'Saving...')}</>
              : t('planning_purchasing.register_n', 'Register {{n}} assets', { n: rows.length })}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default RegisterAssetsModal;
